// src/pages/business/BusinessSettingsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { getBusinessSettings, patchBusinessSettings } from "@/lib/api";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import Header from "@/components/ui/Header";
import Sidebar from "@/components/ui/Sidebar";

/* Domeniul pentru pagina publică (prod: appointiq.ro, dev: poți seta VITE_PUBLIC_BOOKING_DOMAIN) */
const PUBLIC_BOOKING_DOMAIN =
  import.meta.env.VITE_PUBLIC_BOOKING_DOMAIN || "appointiq.ro";

/* ------ UI helpers ------ */
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center py-2">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}


/* =========================================
   Sucursale (read-only, ca în Django Admin)
   ========================================= */
function LocationsPanel({ locations }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border p-4 mb-6">
      <h2 className="font-medium mb-2">{t("business.branches")}</h2>

      {locations.length === 0 ? (
        <div className="text-sm text-gray-600">{t("business.noBranches")}</div>
      ) : (
        <div className="space-y-2 max-w-2xl">
          {locations.map((loc) => (
            <div
              key={loc.id}
              className="flex items-center justify-between p-3 border-b border-b-[#D4D4D4]"
            >
              <div className="flex items-center gap-3">
                <div className="font-medium">{loc.name}</div>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${loc.status === "active"
                    ? "bg-gray-900 text-white"
                    : "bg-gray-200 text-gray-800"
                    }`}
                >
                  {loc.status}
                </span>
              </div>
              <Link
                to={`/business/locations/${loc.id}`}
                className="button px-3 py-2 rounded border"
              >
                {t("common.edit")}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================
   Program de lucru (pliabil, ascuns inițial, lazy-load)
   ========================================= */
function WorkingHoursEditor({ locations }) {
  const { t } = useTranslation();
  const WD_LABELS = t("calendar.weekDaysShort", { returnObjects: true });
  const [open, setOpen] = useState(false); // ASCUNS inițial
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [loadingWH, setLoadingWH] = useState(false);
  const [savingWH, setSavingWH] = useState(false);
  const [msgWH, setMsgWH] = useState("");
  const [wh, setWh] = useState({
    location_id: null,
    // timezone păstrat intern doar dacă vine din API; nu se afișează și nu se trimite
    timezone: "Europe/Bucharest",
    recurring: { "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] },
  });

  // când deschizi prima dată, selectează prima sucursală
  useEffect(() => {
    if (!open) return;
    if (!selectedLocId && locations.length) {
      setSelectedLocId(locations[0].id);
    }
  }, [open, locations, selectedLocId]);

  // încarcă programul pentru sucursala selectată (doar când panoul e deschis)
  useEffect(() => {
    if (!open || !selectedLocId) return;
    let mounted = true;
    setLoadingWH(true);
    (async () => {
      try {
        const { data } = await api.get("/b/working-hours", {
          params: { location_id: String(selectedLocId) },
        });
        if (!mounted) return;
        // normalizare: { "i": [{start,end}] | [] }
        const recurring = {
          "0": [],
          "1": [],
          "2": [],
          "3": [],
          "4": [],
          "5": [],
          "6": [],
        };
        for (let i = 0; i < 7; i++) {
          const k = String(i);
          const lst = Array.isArray(data?.recurring?.[k])
            ? data.recurring[k]
            : [];
          recurring[k] = lst.length
            ? [{ start: lst[0].start, end: lst[0].end }]
            : [];
        }
        setWh({
          location_id: data?.location_id ?? selectedLocId,
          timezone: data?.timezone || "Europe/Bucharest",
          recurring,
        });
        setMsgWH("");
      } catch {
        setMsgWH(t("business.whLoadError"));
      } finally {
        setLoadingWH(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open, selectedLocId]);

  const setClosed = (wd, closed) => {
    const k = String(wd);
    setWh((prev) => ({
      ...prev,
      recurring: {
        ...prev.recurring,
        [k]: closed ? [] : [{ start: "09:00", end: "17:00" }],
      },
    }));
  };

  const setTime = (wd, field, value) => {
    const k = String(wd);
    setWh((prev) => {
      const cur = Array.isArray(prev.recurring[k]) ? prev.recurring[k] : [];
      const item = cur[0] || { start: "09:00", end: "17:00" };
      const next = [{ ...item, [field]: value }];
      return { ...prev, recurring: { ...prev.recurring, [k]: next } };
    });
  };

  const validate = () => {
    const hhmm = /^([01]\d|2[0-3]):([0-5]\d)$/;
    for (let i = 0; i < 7; i++) {
      const k = String(i);
      const lst = wh.recurring[k];
      if (!lst?.length) continue;
      const { start, end } = lst[0] || {};
      if (!hhmm.test(start || "") || !hhmm.test(end || "")) {
        return t("business.whTimeFormatError", { day: WD_LABELS[i] });
      }
      if (start >= end) {
        return t("business.whTimeOrderError", { day: WD_LABELS[i] });
      }
    }
    return null;
  };

  const onSave = async () => {
    if (!selectedLocId) return;
    const err = validate();
    if (err) {
      setMsgWH(err);
      setTimeout(() => setMsgWH(""), 4000);
      return;
    }
    setSavingWH(true);
    setMsgWH("");
    try {
      const payload = {
        location_id: selectedLocId,
        recurring: wh.recurring, // timezone NU se trimite
      };
      await api.put("/b/working-hours", payload);
      setMsgWH(t("business.whUpdated"));
    } catch (e) {
      const detail = e?.response?.data || t("business.saveError");
      setMsgWH(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSavingWH(false);
      setTimeout(() => setMsgWH(""), 4000);
    }
  };

  return (
    <div className="rounded-2xl border p-4 mb-6">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 rounded border"
      >
        <span className="font-medium">{t("calendar.workingHours")}</span>
        <span className="text-sm">{open ? t("business.whToggleHide") : t("business.whToggleShow")}</span>
      </button>

      {!open ? null : (
        <div className="mt-3">
          {locations.length > 1 ? (
            <Row label={t("business.branch")}>
              <select
                className="w-full border rounded px-3 py-2"
                value={selectedLocId || ""}
                onChange={(e) =>
                  setSelectedLocId(Number(e.target.value || 0) || null)
                }
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Row>
          ) : (
            <Row label={t("business.branch")}>
              <div className="text-sm">{locations[0]?.name || "—"}</div>
            </Row>
          )}

          {msgWH ? (
            <div className="mb-3 text-sm px-3 py-2 rounded bg-gray-50 border border-gray-200">
              {msgWH}
            </div>
          ) : null}

          <div className="border rounded-lg divide-y">
            {Array.from({ length: 7 }).map((_, i) => {
              const k = String(i);
              const closed = !(wh.recurring[k]?.length);
              const start = wh.recurring[k]?.[0]?.start || "09:00";
              const end = wh.recurring[k]?.[0]?.end || "17:00";
              return (
                <div
                  key={i}
                  className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center p-2"
                >
                  <div className="text-sm font-medium">{WD_LABELS[i]}</div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={closed}
                      onChange={(e) => setClosed(i, e.target.checked)}
                    />
                    {t("calendar.closed")}
                  </label>
                  <div className="md:col-span-3 grid grid-cols-2 gap-2">
                    <input
                      type="time"
                      disabled={closed}
                      className="w-full border rounded px-3 py-2"
                      value={start}
                      onChange={(e) => setTime(i, "start", e.target.value)}
                    />
                    <input
                      type="time"
                      disabled={closed}
                      className="w-full border rounded px-3 py-2"
                      value={end}
                      onChange={(e) => setTime(i, "end", e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3">
            <button
              onClick={onSave}
              disabled={savingWH || loadingWH || !selectedLocId}
              className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
            >
              {savingWH ? t("business.whSaving") : t("business.whSaveBtn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================
   Main page – TOT din backend
   ========================================= */
export default function BusinessSettingsPage() {
  const { t } = useTranslation();
  const businessId = useMemo(
    () => window.localStorage.getItem("current_business_id") || "1",
    []
  );

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const handleSidebarToggle = () => setSidebarCollapsed((v) => !v);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    name: "",
    slug: "", // read-only în UI
    // fără default_timezone în UI
    default_slot_minutes: 30,
    currency: "RON",
    cancellation_window_hours: 24,
    booking_horizon_days: 60,
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    allow_overbooking: false,
    phone: "",
    email: "",
    locale: "ro-RO",
    country: "RO",
    address: "",
    cui: "",
  });

  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(true);

  // SMS Gateway pairing
  const [smsPairing, setSmsPairing] = useState({
    deviceName: "Telefon SMS",
    pairToken: "",
    expiresAt: "",
    loading: false,
    error: "",
  });

  // Descărcare APK SMS Gateway (link semnat, cerut la apăsarea butonului)
  const [apkState, setApkState] = useState({
    loading: false,
    error: "",
    downloadUrl: "",
    versionName: "",
  });

  // SMS devices status (online / last seen)
  const [smsDevicesState, setSmsDevicesState] = useState({
    items: [],
    onlineWindowSeconds: 120,
    loading: false,
    error: "",
    lastFetchedAt: null,
  });

  const loadSmsDevices = async () => {
    setSmsDevicesState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get("/b/sms/devices");
      setSmsDevicesState((s) => ({
        ...s,
        items: Array.isArray(data?.items) ? data.items : [],
        onlineWindowSeconds: Number(data?.online_window_seconds || 120),
        loading: false,
        lastFetchedAt: new Date().toISOString(),
      }));
    } catch (e) {
      const d = e?.response?.data;
      const msg2 = typeof d === "string" ? d : (d?.detail || JSON.stringify(d || {}));
      setSmsDevicesState((s) => ({
        ...s,
        loading: false,
        error: msg2 || t("business.smsDevicesLoadError"),
      }));
    }
  };

  const loadLocations = async () => {
    setLocLoading(true);
    try {
      const { data } = await api.get("/b/locations");
      setLocations(Array.isArray(data) ? data : []);
    } finally {
      setLocLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await getBusinessSettings(businessId);
        if (!mounted) return;
        setForm((prev) => ({ ...prev, ...settings }));
      } catch (e) {
        console.error(e);
        setMsg(t("business.settingsLoadError"));
      } finally {
        setLoading(false);
      }
    })();
    loadLocations(); // paralel

    // SMS device status (best effort)
    loadSmsDevices();
    const t = setInterval(loadSmsDevices, 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [businessId]);

  const onChange = (k) => (e) => {
    const v =
      e?.target?.type === "checkbox"
        ? e.target.checked
        : e?.target?.value ?? e;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const onNumber = (k) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [k]: v === "" ? "" : Number(v) }));
  };

  const onSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      const payload = { ...form };
      delete payload.branches_count;
      delete payload.default_timezone; // nu trimitem timezone
      delete payload.slug; // slug-ul e read-only în acest UI

      const saved = await patchBusinessSettings(payload, businessId);
      setForm((prev) => ({ ...prev, ...saved }));
      setMsg(t("business.savedSuccess"));
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data || t("business.saveError");
      setMsg(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          isCollapsed={sidebarCollapsed}
          onToggleSidebar={handleSidebarToggle}
        />
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <main
          className={`transition-all duration-300 ease-out-custom page-top pb-20 lg:pb-6 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
            }`}
        >
          <div className="max-w-3xl mx-auto p-6 text-gray-600">
            {t("business.settingsLoading")}
          </div>
        </main>
      </div>
    );
  }


  const publicUrl =
    form.slug && form.slug.trim()
      ? `https://${form.slug}.${PUBLIC_BOOKING_DOMAIN}`
      : "";

  const handleCopyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setMsg(t("business.publicUrlCopied"));
      setTimeout(() => setMsg(""), 3000);
    } catch {
      setMsg(t("business.copyError"));
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const handleGenerateSmsPairToken = async () => {
    setSmsPairing((p) => ({ ...p, loading: true, error: "" }));
    try {
      const { data } = await api.post("/b/sms/pair-token", {
        device_name: smsPairing.deviceName || "Telefon SMS",
        expires_minutes: 30,
      });

      setSmsPairing((p) => ({
        ...p,
        pairToken: String(data?.pair_token || ""),
        expiresAt: data?.expires_at ? String(data.expires_at) : "",
      }));
    } catch (e) {
      const d = e?.response?.data;
      const msg2 = typeof d === "string" ? d : (d?.detail || JSON.stringify(d || {}));
      setSmsPairing((p) => ({ ...p, error: msg2 || t("business.smsGenerateError") }));
    } finally {
      setSmsPairing((p) => ({ ...p, loading: false }));
    }
  };

  const handleCopySmsPairToken = async () => {
    if (!smsPairing.pairToken) return;
    try {
      await navigator.clipboard.writeText(smsPairing.pairToken);
      setMsg(t("business.smsPairCopied"));
      setTimeout(() => setMsg(""), 3000);
    } catch {
      setMsg(t("business.smsCopyError"));
      setTimeout(() => setMsg(""), 3000);
    }
  };

  // Link de descărcare pentru APK-ul SMS Gateway.
  // Aplicația nu e pe Google Play, iar APK-ul e parte din feature-ul plătit "sms",
  // deci serverul dă un link semnat, valabil ~30 min, pe care îl arătăm ca QR:
  // clientul scanează cu telefonul și descarcă, fără să se logheze pe telefon.
  const handleRequestApkLink = async () => {
    setApkState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.post("/b/sms/apk-token", {});
      setApkState({
        loading: false,
        error: "",
        downloadUrl: String(data?.download_url || ""),
        versionName: String(data?.version_name || ""),
      });
    } catch (e) {
      const d = e?.response?.data;
      const detail = typeof d === "string" ? d : (d?.detail || "");
      setApkState((s) => ({
        ...s,
        loading: false,
        error:
          detail ||
          "Nu am putut genera linkul de descărcare. Verifică dacă abonamentul include SMS.",
      }));
    }
  };

  const handleCopyApkLink = async () => {
    if (!apkState.downloadUrl) return;
    try {
      await navigator.clipboard.writeText(apkState.downloadUrl);
      setMsg("Linkul de descărcare a fost copiat.");
      setTimeout(() => setMsg(""), 3000);
    } catch {
      setMsg(t("business.copyError"));
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const smsPairLink = (() => {
    if (!smsPairing.pairToken) return "";
    const baseUrl = "https://appointiq.ro/api/";
    const qs = new URLSearchParams({
      pair_token: smsPairing.pairToken,
      base_url: baseUrl,
    });
    return `${window.location.origin}/api/sms/pair-link?${qs.toString()}`;
  })();

  const handleCopySmsPairLink = async () => {
    if (!smsPairLink) return;
    try {
      await navigator.clipboard.writeText(smsPairLink);
      setMsg(t("business.smsPairLinkCopied"));
      setTimeout(() => setMsg(""), 3000);
    } catch {
      setMsg(t("business.copyError"));
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const formatLastSeen = (iso) => {
    if (!iso) return t("business.never");
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);

    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin <= 0) return t("business.agoNow");
    if (diffMin < 60) return t("business.agoMin", { count: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return t("business.agoHours", { count: diffH });
    const diffD = Math.floor(diffH / 24);
    if (diffD < 14) return t("business.agoDays", { count: diffD });
    return d.toLocaleString("ro-RO");
  };


  return (
    <div className="min-h-screen bg-background">
      <Header
        isCollapsed={sidebarCollapsed}
        onToggleSidebar={handleSidebarToggle}
      />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main
        className={`transition-all duration-300 ease-out-custom page-top pb-20 lg:pb-6 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
      >
        <div className="max-w-3xl mx-auto p-6">
          <h1 className="text-xl font-semibold mb-4">{t("business.pageTitle")}</h1>

          {msg ? (
            <div className="mb-4 text-sm px-3 py-2 rounded bg-gray-50 border border-gray-200">
              {msg}
            </div>
          ) : null}

          {/* General */}
          <div className="rounded-2xl border p-4 mb-6">
            <h2 className="font-medium mb-2">{t("business.general")}</h2>
            <Row label={t("business.name")}>
              <input
                className="w-full border rounded px-3 py-2"
                value={form.name || ""}
                onChange={onChange("name")}
                placeholder="Ex: Barberia Sorin"
                required
              />
            </Row>

            <Row label={t("business.slotDuration")}>
              <select
                className="w-full border rounded px-3 py-2"
                value={form.default_slot_minutes ?? 30}
                onChange={onNumber("default_slot_minutes")}
              >
                {[5, 10, 15, 20, 30, 45, 60, 90, 120].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Row>

            <Row label={t("business.slugLabel")}>
              <input
                className="w-full border rounded px-3 py-2"
                value={form.slug || ""}
                readOnly
                placeholder="Va fi generat automat din nume"
              />
            </Row>

            <Row label={t("business.publicUrlLabel")}>
              {publicUrl ? (
                <div className="flex flex-col gap-2">
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:underline break-all"
                  >
                    {publicUrl}
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyPublicUrl}
                    className="inline-flex items-center w-fit px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                  >
                    {t("business.copyLink")}
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  {t("business.slugAutoGenerated")}
                </div>
              )}
            </Row>
          </div>

          {/* Sucursale (read-only, din DB) */}
          <div className="rounded-2xl border p-4 mb-6">
            {locLoading ? (
              <div className="text-sm text-gray-600">
                {t("business.branchesLoading")}
              </div>
            ) : (
              <LocationsPanel locations={locations} />
            )}
          </div>

          {/* Program de lucru (ASCUNS inițial, lazy-load) */}
          <WorkingHoursEditor locations={locations} />

          {/* Contact */}
          <div className="rounded-2xl border p-4 mb-6">
            <h2 className="font-medium mb-2">{t("business.contact")}</h2>
            <Row label={t("common.phone")}>
              <input
                className="w-full border rounded px-3 py-2"
                value={form.phone || ""}
                onChange={onChange("phone")}
              />
            </Row>
            <Row label={t("common.email")}>
              <input
                className="w-full border rounded px-3 py-2"
                value={form.email || ""}
                onChange={onChange("email")}
              />
            </Row>
            <Row label={t("common.address")}>
              <input
                className="w-full border rounded px-3 py-2"
                value={form.address || ""}
                onChange={onChange("address")}
              />
            </Row>
            <Row label={t("business.cuiLabel")}>
              <input
                className="w-full border rounded px-3 py-2"
                placeholder={t("business.cuiPlaceholder")}
                value={form.cui || ""}
                onChange={onChange("cui")}
              />
            </Row>
          </div>

          {/* SMS Gateway */}
          <div className="rounded-2xl border p-4 mb-6">
            <h2 className="font-medium mb-2">{t("business.smsGateway")}</h2>
            <div className="text-sm text-gray-600 mb-3">
              {t("business.smsDesc")}
            </div>

            {smsPairing.error ? (
              <div className="mb-3 text-sm px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700">
                {smsPairing.error}
              </div>
            ) : null}

            {/* Pasul 1: instalarea aplicației pe telefon */}
            <Row label="1. Instalează aplicația">
              <div className="flex flex-col gap-3">
                <div className="text-sm text-text-secondary">
                  Aplicația nu e pe Google Play. Generează un link de descărcare și
                  scanează-l cu telefonul care va trimite SMS-urile.
                </div>

                {apkState.error ? (
                  <div className="text-sm px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    {apkState.error}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={handleRequestApkLink}
                  disabled={apkState.loading}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  {apkState.loading
                    ? "Se generează..."
                    : apkState.downloadUrl
                      ? "Generează alt link de descărcare"
                      : "Generează link de descărcare"}
                </button>

                {apkState.downloadUrl ? (
                  <div className="flex flex-col items-center gap-3 p-4 rounded-xl border border-border bg-muted/40">
                    <div className="bg-white p-3 rounded-lg border border-border shadow-sm">
                      <QRCodeSVG value={apkState.downloadUrl} size={200} level="M" />
                    </div>

                    <div className="text-center">
                      <div className="text-sm font-medium text-foreground">
                        Scanează cu telefonul ca să descarci aplicația
                      </div>
                      <div className="text-xs text-text-secondary mt-1">
                        {apkState.versionName ? `Versiunea ${apkState.versionName}. ` : ""}
                        Telefonul va cere permisiunea de a instala din afara Google Play — acceptă.
                      </div>
                      <div className="text-xs text-text-secondary mt-2">
                        Linkul expiră în 30 de minute.
                      </div>
                    </div>

                    <div className="w-full border-t border-border pt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={handleCopyApkLink}
                        className="flex-1 px-3 py-2 rounded border border-border bg-background text-foreground text-xs hover:bg-muted"
                      >
                        {t("business.copyLinkBtn")}
                      </button>
                      <a
                        href={apkState.downloadUrl}
                        className="flex-1 px-3 py-2 rounded border border-border bg-background text-foreground text-xs hover:bg-muted text-center"
                      >
                        {t("business.openOnPhone")}
                      </a>
                    </div>
                  </div>
                ) : null}
              </div>
            </Row>

            <Row label={t("business.deviceName")}>
              <input
                className="w-full border rounded px-3 py-2"
                value={smsPairing.deviceName}
                onChange={(e) =>
                  setSmsPairing((p) => ({ ...p, deviceName: e.target.value }))
                }
                placeholder="Ex: Telefon receptie"
              />
            </Row>

            <Row label={t("business.deviceStatus")}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={loadSmsDevices}
                    disabled={smsDevicesState.loading}
                    className="px-3 py-2 rounded border border-border bg-background text-foreground text-sm hover:bg-muted disabled:opacity-50"
                  >
                    {smsDevicesState.loading ? t("business.smsChecking") : t("business.refresh")}
                  </button>
                  {smsDevicesState.lastFetchedAt ? (
                    <div className="text-xs text-text-secondary">
                      {t("business.lastRefresh")}{" "}
                      {new Date(smsDevicesState.lastFetchedAt).toLocaleTimeString(
                        "ro-RO"
                      )}
                    </div>
                  ) : null}
                </div>

                {smsDevicesState.error ? (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    {smsDevicesState.error}
                  </div>
                ) : null}

                {smsDevicesState.loading &&
                  (!smsDevicesState.items || smsDevicesState.items.length === 0) ? (
                  <div className="text-sm text-text-secondary">
                    {t("business.checkingStatus")}
                  </div>
                ) : Array.isArray(smsDevicesState.items) &&
                  smsDevicesState.items.length ? (
                  <div className="space-y-2">
                    {smsDevicesState.items.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {d.name || `Device #${d.id}`}
                          </div>
                          <div className="text-xs text-text-secondary">
                            {t("business.lastSeen")} {formatLastSeen(d.last_seen_at)}
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${d.online
                              ? "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
                              : "border-border bg-muted text-text-secondary"
                            }`}
                          title={`Online window: ~${Math.round(
                            (smsDevicesState.onlineWindowSeconds || 120) / 60
                          )} min`}
                        >
                          {d.online ? "Online" : "Offline"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-text-secondary">
                    {t("business.noDevices")}
                  </div>
                )}
              </div>
            </Row>

            <Row label={`2. ${t("business.connectPhone")}`}>
              <div className="flex flex-col gap-3">
                {/* Buton principal */}
                <button
                  type="button"
                  onClick={handleGenerateSmsPairToken}
                  disabled={smsPairing.loading}
                  className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {smsPairing.loading ? t("business.generating") : smsPairing.pairToken ? t("business.regenerateQr") : t("business.generateQr")}
                </button>

                {/* QR code + instrucțiuni */}
                {smsPairLink ? (
                  <div className="flex flex-col items-center gap-3 p-4 rounded-xl border border-border bg-muted/40">
                    <div className="bg-white p-3 rounded-lg border border-border shadow-sm">
                      <QRCodeSVG
                        value={smsPairLink}
                        size={200}
                        level="M"
                      />
                    </div>

                    <div className="text-center">
                      <div className="text-sm font-medium text-foreground">
                        {t("business.scanQr")}
                      </div>
                      <div className="text-xs text-text-secondary mt-1">
                        {t("business.scanQrDesc")}
                      </div>
                      {smsPairing.expiresAt ? (
                        <div className="text-xs text-text-secondary mt-2">
                          {t("business.expires")} {new Date(smsPairing.expiresAt).toLocaleString("ro-RO")}
                        </div>
                      ) : null}
                    </div>

                    {/* Opțiuni secundare */}
                    <div className="w-full border-t border-border pt-3 flex flex-col gap-2">
                      <div className="text-xs text-text-secondary text-center mb-1">
                        {t("business.orScanLink")}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleCopySmsPairLink}
                          className="flex-1 px-3 py-2 rounded border border-border bg-background text-foreground text-xs hover:bg-muted"
                        >
                          {t("business.copyLinkBtn")}
                        </button>
                        <a
                          href={smsPairLink}
                          className="flex-1 px-3 py-2 rounded border border-border bg-background text-foreground text-xs hover:bg-muted text-center"
                        >
                          {t("business.openOnPhone")}
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-text-secondary text-center">
                    {t("business.generateQrHint")}
                  </div>
                )}
              </div>
            </Row>

            <Row label="API URL">
              <div className="text-sm text-text-secondary break-all">
                {window.location.origin}/api
              </div>
            </Row>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={saving}
              className="px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? t("business.saving") : t("business.saveSettings")}
            </button>
          </div>
        </div>
      </main>
    </div>
  );

}
